import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Block as NaIcon,
  Check as PassIcon,
  Checklist as QaIcon,
  Close as FailIcon,
  Delete as DeleteIcon,
  Download as ImportIcon,
  Edit as EditIcon,
  EditNote as RecordIcon,
  LocalParking as ParkingQaIcon,
  MoreVert as MoreIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';

import ProjectPageShell from '../ProjectDashboardPage/ProjectPageShell';
import ParkingQaSection from '../ProjectDashboardPage/sections/ParkingQaSection';
import QaNotesPanel from '../../components/qa/QaNotesPanel';
import QaItemFormDialog, { QaItemFormValue } from '../../components/qa/QaItemFormDialog';
import QaResultDialog from '../../components/qa/QaResultDialog';
import QaImportDialog from '../../components/qa/QaImportDialog';
import {
  QaImportChoice,
  QaResult,
  QaTemplateItemInput,
  SiteQaItem,
  SiteQaItemUpdate,
  SiteQaService,
} from '../../services/SiteQaService';
import usePermissions from '../../hooks/usePermissions';
import { getUserInfo } from '../../utils/auth';

// 탭 — 기본은 '진행할 항목'(QA 가능 & 아직 성공 못 한 것). 완료·불가는 따로 모아 본다.
type View = 'todo' | 'done' | 'na' | 'all';

const inView: Record<View, (it: SiteQaItem) => boolean> = {
  todo: (it) => it.applicable && it.result !== 'pass',
  done: (it) => it.applicable && it.result === 'pass',
  na: (it) => !it.applicable,
  all: () => true,
};

const VIEW_LABEL: Record<View, string> = {
  todo: '진행할 항목',
  done: '완료',
  na: 'QA 불가',
  all: '전체',
};

const EMPTY_TEXT: Record<View, string> = {
  todo: '진행할 항목이 없습니다. 가능한 QA 를 모두 마쳤습니다.',
  done: '아직 성공한 항목이 없습니다.',
  na: 'QA 불가로 둔 항목이 없습니다.',
  all: '',
};

function today(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function StatusChip({ it }: { it: SiteQaItem }) {
  if (!it.applicable) return <Chip size="small" color="warning" variant="outlined" label="불가" />;
  if (it.result === 'pass') return <Chip size="small" color="success" label="성공" />;
  if (it.result === 'fail') return <Chip size="small" color="error" label="실패" />;
  return <Chip size="small" variant="outlined" label="미진행" />;
}

type Preset = { result?: QaResult; applicable?: boolean };

/**
 * 현장 QA — 공통 QA 리스트를 가져와(항목별 QA 가능/불가·불가 사유 선택) 결과를 기록한다.
 * 라우트: /project/:projectId/qa (현장 사이드바 'QA')
 *
 * 조회는 누구나, 기록·가져오기·항목 추가는 editor 이상 (백엔드 RequireRole 과 같다).
 * [주차 테스트] 탭(?tab=parking)은 현장 주차면 점유를 직접 바꾸므로 editor 이상에게만 보인다.
 */
const SiteQaPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const projectIdNum = projectId ? Number(projectId) : NaN;
  const { canEdit } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const section = canEdit && searchParams.get('tab') === 'parking' ? 'parking' : 'list';

  const [items, setItems] = useState<SiteQaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [view, setView] = useState<View>('todo');
  const [importOpen, setImportOpen] = useState(false);
  const [recording, setRecording] = useState<{ item: SiteQaItem; preset?: Preset } | null>(null);
  const [itemDialog, setItemDialog] = useState<{ open: boolean; editing: SiteQaItem | null }>({
    open: false,
    editing: null,
  });
  const [menu, setMenu] = useState<{ anchor: HTMLElement; item: SiteQaItem } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(projectIdNum)) return;
    setLoading(true);
    setError(null);
    try {
      setItems(await SiteQaService.listSite(projectIdNum));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [projectIdNum]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(
    () => ({
      todo: items.filter(inView.todo).length,
      done: items.filter(inView.done).length,
      na: items.filter(inView.na).length,
      all: items.length,
      fail: items.filter((it) => it.applicable && it.result === 'fail').length,
    }),
    [items]
  );
  const target = counts.all - counts.na;

  // 분류별로 묶는다 — 순서는 목록(등록 순)에 처음 나온 분류 순.
  const groups = useMemo(() => {
    const order: string[] = [];
    const byCat = new Map<string, SiteQaItem[]>();
    items.filter(inView[view]).forEach((it) => {
      const k = it.category || '기타';
      if (!byCat.has(k)) {
        byCat.set(k, []);
        order.push(k);
      }
      byCat.get(k)!.push(it);
    });
    return order.map((k) => ({ category: k, items: byCat.get(k)! }));
  }, [items, view]);

  const existingTemplateIds = useMemo(
    () => items.map((it) => it.template_item_id).filter((v): v is number => typeof v === 'number'),
    [items]
  );

  const replace = (updated: SiteQaItem) =>
    setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));

  const saveResult = async (id: number, value: SiteQaItemUpdate) => {
    replace(await SiteQaService.updateSite(id, value));
  };

  const runRow = async (it: SiteQaItem, value: SiteQaItemUpdate, done?: string) => {
    setBusyId(it.id);
    setError(null);
    try {
      await saveResult(it.id, value);
      if (done) setToast(done);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  // 목록에서 바로 [성공] — 날짜는 오늘, 담당자가 비었으면 누른 사람.
  const quickPass = (it: SiteQaItem) =>
    runRow(
      it,
      { result: 'pass', passed_at: today(), assignee: it.assignee || getUserInfo()?.name || '' },
      `'${it.title}' 성공으로 기록했습니다`
    );

  const undoResult = (it: SiteQaItem) => runRow(it, { applicable: true, result: 'pending' });

  const doImport = async (choices: QaImportChoice[]) => {
    const r = await SiteQaService.importTemplate(projectIdNum, choices);
    setItems(r.items);
    setToast(r.message);
    setView('todo');
  };

  const saveItem = async (value: QaTemplateItemInput) => {
    if (itemDialog.editing) {
      replace(await SiteQaService.updateSite(itemDialog.editing.id, value));
    } else {
      await SiteQaService.createSite(projectIdNum, value);
      await load();
    }
  };

  const remove = async (it: SiteQaItem) => {
    if (!window.confirm(`'${it.title}' 항목과 기록을 이 현장에서 삭제할까요?`)) return;
    try {
      await SiteQaService.deleteSite(it.id);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const itemInitial = useMemo<Partial<QaItemFormValue> | undefined>(() => {
    const t = itemDialog.editing;
    if (!t) return undefined;
    return { category: t.category, title: t.title, method: t.method, assignee: t.assignee };
  }, [itemDialog.editing]);

  const closeMenuAnd = (fn: (it: SiteQaItem) => void) => () => {
    if (menu) fn(menu.item);
    setMenu(null);
  };

  const renderDetail = (it: SiteQaItem) => {
    if (!it.applicable) {
      return (
        <Typography variant="body2" color="warning.main">
          불가 사유: {it.unavailable_reason || '—'}
        </Typography>
      );
    }
    if (it.result === 'fail') {
      return (
        <Typography variant="body2" color="error.main">
          실패 사유: {it.fail_reason || '—'}
        </Typography>
      );
    }
    if (it.result === 'pass') {
      return (
        <Typography variant="body2" color="success.main">
          {it.passed_at || ''} 성공
        </Typography>
      );
    }
    return null;
  };

  const sectionTabs = canEdit && (
    <Tabs
      value={section}
      onChange={(_, v: string) => setSearchParams(v === 'parking' ? { tab: 'parking' } : {}, { replace: true })}
      sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', minHeight: 44 }}
      data-testid="qa-section-tabs"
    >
      <Tab
        value="list"
        icon={<QaIcon fontSize="small" />}
        iconPosition="start"
        label="QA 리스트"
        data-testid="qa-section-list"
        sx={{ minHeight: 44, textTransform: 'none', fontWeight: 700 }}
      />
      <Tab
        value="parking"
        icon={<ParkingQaIcon fontSize="small" />}
        iconPosition="start"
        label="주차 테스트"
        data-testid="qa-section-parking"
        sx={{ minHeight: 44, textTransform: 'none', fontWeight: 700 }}
      />
    </Tabs>
  );

  return (
    <ProjectPageShell
      projectId={projectId ?? ''}
      activeSection="qa"
      breadcrumbExtra={
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
          <QaIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          QA
        </Typography>
      }
      hideSettingsButton
    >
      {!Number.isFinite(projectIdNum) ? (
        <Typography color="error">유효하지 않은 프로젝트 ID</Typography>
      ) : section === 'parking' ? (
        <Box>
          {sectionTabs}
          <ParkingQaSection key={projectIdNum} projectId={projectIdNum} />
          <QaNotesPanel key={`notes-${projectIdNum}`} projectId={projectIdNum} />
        </Box>
      ) : (
        <Box>
          {sectionTabs}
          {/* 제목 + 진행률 + 버튼 */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'flex-end' }}
            sx={{ mb: 2 }}
          >
            <Box sx={{ flex: 1, maxWidth: 520 }}>
              <Typography variant="h6" fontWeight={700}>
                현장 QA
              </Typography>
              {target > 0 ? (
                <>
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" data-testid="qa-progress-text">
                      QA 대상 {target}개 중 <b>{counts.done}개 성공</b>
                      {counts.fail > 0 && (
                        <Typography component="span" variant="body2" color="error.main">
                          {' '}
                          · 실패 {counts.fail}개
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {Math.round((counts.done / target) * 100)}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={(counts.done / target) * 100}
                    color="success"
                    sx={{ mt: 0.75, height: 8, borderRadius: 4 }}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  공통 QA 리스트를 가져와 항목별로 결과를 기록합니다.
                </Typography>
              )}
            </Box>
            {canEdit && (
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<ImportIcon />}
                  onClick={() => setImportOpen(true)}
                  data-testid="qa-import"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  공통 QA 가져오기
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setItemDialog({ open: true, editing: null })}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  현장 항목 추가
                </Button>
              </Stack>
            )}
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">아직 QA 항목이 없습니다.</Typography>
              {canEdit && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  [공통 QA 가져오기]로 공통 리스트를 불러오세요.
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <Tabs
                value={view}
                onChange={(_, v: View) => setView(v)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
                data-testid="qa-tabs"
              >
                {(['todo', 'done', 'na', 'all'] as View[]).map((v) => (
                  <Tab
                    key={v}
                    value={v}
                    label={`${VIEW_LABEL[v]} ${counts[v]}`}
                    data-testid={`qa-tab-${v}`}
                    sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }}
                  />
                ))}
              </Tabs>

              {groups.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
                  {EMPTY_TEXT[view]}
                </Typography>
              ) : (
                <Box data-testid="site-qa-list">
                  {groups.map((g) => (
                    <Box key={g.category} sx={{ mt: 2 }}>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ fontWeight: 700, letterSpacing: 0.5, lineHeight: 2 }}
                      >
                        {g.category} · {g.items.length}
                      </Typography>
                      <Divider />
                      {g.items.map((it) => {
                        const busy = busyId === it.id;
                        const open = it.applicable && it.result !== 'pass';
                        return (
                          <Box key={it.id} data-testid={`qa-row-${it.id}`}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={{ xs: 1, sm: 2 }}
                              alignItems={{ xs: 'stretch', sm: 'center' }}
                              sx={{ py: 1.25 }}
                            >
                              <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ width: 56, flexShrink: 0, pt: 0.25 }}>
                                <StatusChip it={it} />
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {it.title}
                                </Typography>
                                {it.method && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', whiteSpace: 'pre-wrap' }}
                                  >
                                    {it.method}
                                  </Typography>
                                )}
                                {renderDetail(it)}
                                {it.note && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    비고: {it.note}
                                  </Typography>
                                )}
                              </Box>
                              </Stack>
                              <Typography
                                variant="body2"
                                color={it.assignee ? 'text.primary' : 'text.disabled'}
                                sx={{ width: { sm: 110 }, flexShrink: 0, pl: { xs: 9, sm: 0 } }}
                                title={it.updated_by_name ? `마지막 수정: ${it.updated_by_name}` : undefined}
                              >
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.75 }}>
                                  담당
                                </Typography>
                                {it.assignee || '미지정'}
                              </Typography>
                              {canEdit && (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                  sx={{ flexShrink: 0, pl: { xs: 9, sm: 0 } }}
                                >
                                  {open ? (
                                    <>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <PassIcon />}
                                        disabled={busy}
                                        onClick={() => quickPass(it)}
                                        data-testid={`qa-pass-${it.id}`}
                                        sx={{ textTransform: 'none', minWidth: 72 }}
                                      >
                                        성공
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        startIcon={<FailIcon />}
                                        disabled={busy}
                                        onClick={() => setRecording({ item: it, preset: { applicable: true, result: 'fail' } })}
                                        data-testid={`qa-fail-${it.id}`}
                                        sx={{ textTransform: 'none', minWidth: 72, whiteSpace: 'nowrap' }}
                                      >
                                        {it.result === 'fail' ? '사유 수정' : '실패'}
                                      </Button>
                                    </>
                                  ) : (
                                    <Button
                                      size="small"
                                      color="inherit"
                                      startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <UndoIcon />}
                                      disabled={busy}
                                      onClick={() => undoResult(it)}
                                      data-testid={`qa-undo-${it.id}`}
                                      sx={{ textTransform: 'none', minWidth: 148, color: 'text.secondary' }}
                                    >
                                      {it.applicable ? '미진행으로' : '가능으로 되돌리기'}
                                    </Button>
                                  )}
                                  <IconButton
                                    size="small"
                                    aria-label="더보기"
                                    onClick={(e) => setMenu({ anchor: e.currentTarget, item: it })}
                                    data-testid={`qa-more-${it.id}`}
                                  >
                                    <MoreIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              )}
                            </Stack>
                            <Divider />
                          </Box>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem onClick={closeMenuAnd((it) => setRecording({ item: it }))}>
          <ListItemIcon>
            <RecordIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>결과·담당자·비고 기록</ListItemText>
        </MenuItem>
        {menu?.item.applicable && (
          <MenuItem
            onClick={closeMenuAnd((it) => setRecording({ item: it, preset: { applicable: false } }))}
            data-testid="qa-menu-na"
          >
            <ListItemIcon>
              <NaIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>QA 불가로 바꾸기</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={closeMenuAnd((it) => setItemDialog({ open: true, editing: it }))}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>항목 내용 수정</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={closeMenuAnd(remove)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>이 현장에서 삭제</ListItemText>
        </MenuItem>
      </Menu>

      <QaImportDialog
        open={importOpen}
        existingTemplateIds={existingTemplateIds}
        onClose={() => setImportOpen(false)}
        onImport={doImport}
      />
      <QaResultDialog
        item={recording?.item ?? null}
        preset={recording?.preset}
        onClose={() => setRecording(null)}
        onSubmit={saveResult}
      />
      <QaItemFormDialog
        open={itemDialog.open}
        title={itemDialog.editing ? 'QA 항목 내용 수정' : '현장 QA 항목 추가'}
        initial={itemInitial}
        onClose={() => setItemDialog({ open: false, editing: null })}
        onSubmit={saveItem}
      />
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </ProjectPageShell>
  );
};

export default SiteQaPage;
